import { useState, useCallback, useEffect } from "react";
import { Validate } from "api/interfaces/commonInterface.interface";
import { ValidateMsg } from "api/interfaces/commonInterface.interface";
import {
  SelectGroup,
  VideoGuideGroupDto,
} from "api/interfaces/managerInterface.interface";
import useHelpGuideApi, { HelpGuideGroup } from "hook/useHelpGuideApi";
import AddGroupPresenter from "./AddGroupPresenter";
import { type Option } from "utils/options";
import useApiError from "hook/useApiError";

interface Props {
  isEdit?: boolean; // 편집 모드일 때
  selectedGroup?: SelectGroup;
  onModal: (type?: string) => void;
  onClickDeactive?: (groupId: string) => void;
  maxPosition: number;
  getHelpGuideGroups: () => void;
  groups: HelpGuideGroup[];
}

export enum UPDATE_TYPE {
  CATEGORY,
  GROUP_NAME,
  POSITION,
  LAYOUT,
}

const initialGroupInfo = {
  category: "",
  name: "",
  groupId: "",
  position: 1,
  layout: "slide",
};

const layoutOptions = [
  { label: "Video", value: "slide" },
  { label: "File", value: "table" },
];

function AddGroupContainer(props: Props): JSX.Element {
  const [isDeleteConfirm, setIsDeleteConfirm] = useState(false);
  const { updateHelpGuideGroup, addHelpGuideGroup } = useHelpGuideApi();
  const [validate, setValidate] = useState<Validate>({
    name: false,
  });
  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
    name: "",
  });

  const [groupInfo, setGroupInfo] = useState<VideoGuideGroupDto>({
    ...initialGroupInfo,
  });
  const { deleteHelpGuideGroup } = useHelpGuideApi();
  const [isInProcess, setIsInProcess] = useState<boolean>(false);
  const { lang, notifyError, notifySuccess } = useApiError();
  useEffect(() => {
    if (props.isEdit) {
      setGroupInfo({
        category: initialGroupInfo.category,
        name: props.selectedGroup ? props.selectedGroup.groupName : "",
        groupId: props.selectedGroup ? props.selectedGroup.groupId : "",
        position: props.selectedGroup ? props.selectedGroup.position : 1,
        layout: props.selectedGroup ? props.selectedGroup.layout : "slide",
      });
    } else {
      setGroupInfo({
        ...initialGroupInfo,
        position: props.maxPosition,
      });
    }
  }, [props.isEdit, props.selectedGroup, props.maxPosition]);
  const onChangeGroupInfo = useCallback(
    (type: UPDATE_TYPE, value: string | number | string[]) => {
      if (type === UPDATE_TYPE.GROUP_NAME) {
        setGroupInfo((info) => {
          return { ...info, name: value as string };
        });
      } else if (type === UPDATE_TYPE.CATEGORY) {
        setGroupInfo((info) => {
          return { ...info, category: value as string };
        });
      } else if (type === UPDATE_TYPE.POSITION) {
        setGroupInfo((info) => {
          return { ...info, position: value as number };
        });
      } else if (type === UPDATE_TYPE.LAYOUT) {
        setGroupInfo((info) => {
          return { ...info, layout: value as string };
        });
      }
    },
    []
  );

  const onClickDelete = useCallback(() => {
    setIsDeleteConfirm(false);
    if (!props.selectedGroup) {
      return;
    }
    const group: HelpGuideGroup = {
      groupName: props.selectedGroup.groupName,
      groupId: props.selectedGroup.groupId,
      sortOrder: props.selectedGroup.position || 1,
      layout: props.selectedGroup.layout || "slide",
      id: props.selectedGroup.groupId,
    };
    setIsInProcess(true);
    deleteHelpGuideGroup(group)
      .then((res) => {
        setIsInProcess(false);
        notifySuccess(lang.label.manager.video.group.delete.success);
        props.onModal("edit");
        props.getHelpGuideGroups();
      })
      .catch((err) => {
        setIsInProcess(false);
        notifyError(lang.label.manager.video.group.delete.fail);
      });
  }, []);

  const onSaveBefore = useCallback(async () => {
    try {
      setIsInProcess(true);
      const update = props.isEdit;
      if (!groupInfo.name) {
        notifyError(lang.validateMsg.user.group.name.empty);
        return;
      }
      if (!update && props.groups.find((g) => g.groupName === groupInfo.name)) {
        notifyError(lang.validateMsg.user.group.name.exist);
        return;
      }

      const item: HelpGuideGroup = {
        groupId: update ? groupInfo.groupId || "" : "",
        groupName: groupInfo.name,
        layout: groupInfo.layout || "slide",
        sortOrder: groupInfo.position || 0,
      };

      const action = update ? updateHelpGuideGroup : addHelpGuideGroup;
      action(item)
        .then(() => {
          notifySuccess(
            update
              ? lang.label.manager.video.group.modify.success
              : lang.label.manager.video.group.create.success
          );
          props.getHelpGuideGroups();
          props.onModal();
        })
        .catch(() => {
          notifyError(
            update
              ? lang.label.manager.video.group.modify.fail
              : lang.label.manager.video.group.create.fail
          );
        });
    } catch (error) {
      const type = (error as any).type;
      const message = (error as any).message;
      setValidate((info) => {
        return {
          ...info,
          [type]: true,
        };
      });
      setValidateMsg((info) => {
        return {
          ...info,
          [type]: message,
        };
      });
    } finally {
      setIsInProcess(false);
    }
  }, [groupInfo, props]);

  return (
    <AddGroupPresenter
      isEdit={props.isEdit || false}
      isDeleteConfirm={isDeleteConfirm}
      setIsDeleteConfirm={setIsDeleteConfirm}
      onClickDelete={onClickDelete}
      isInProcess={isInProcess}
      onModal={props.onModal}
      groupInfo={groupInfo}
      onChangeGroupInfo={onChangeGroupInfo}
      validate={validate}
      validateMsg={validateMsg}
      layoutOptions={layoutOptions}
      maxPosition={props.maxPosition}
      onSaveBefore={onSaveBefore}
    />
  );
}

export default AddGroupContainer;
